import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useStore from '../store/useStore';
import { 
  FileText, Upload, Trash2, Edit2, Download, Eye, Save, X, 
  Plus, ArrowLeft, File, Loader2, Check, AlertCircle,
  Globe
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const DocumentsPage = () => {
  const { language } = useStore();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    title_fr: '',
    title_en: '',
    title_nl: '',
    description_fr: '',
    description_en: '',
    description_nl: '',
    category: 'general',
    is_public: true,
    file: null
  });

  const categories = [
    { value: 'general', label: { fr: 'Général', en: 'General', nl: 'Algemeen' } },
    { value: 'legal', label: { fr: 'Documents légaux', en: 'Legal documents', nl: 'Juridische documenten' } },
    { value: 'guides', label: { fr: 'Guides', en: 'Guides', nl: 'Gidsen' } },
    { value: 'catalog', label: { fr: 'Catalogues', en: 'Catalogs', nl: 'Catalogi' } },
  ];

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ebb_token');
      const response = await fetch(`${BACKEND_URL}/api/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError(language === 'fr' ? 'Type de fichier non autorisé. Utilisez PDF, JPG, PNG ou WebP.' : 'Invalid file type. Use PDF, JPG, PNG or WebP.');
        return;
      }
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError(language === 'fr' ? 'Fichier trop volumineux (max 10 MB)' : 'File too large (max 10 MB)');
        return;
      }
      setFormData({ ...formData, file });
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('ebb_token');
      const data = new FormData();
      
      data.append('title_fr', formData.title_fr);
      data.append('title_en', formData.title_en);
      data.append('title_nl', formData.title_nl);
      data.append('description_fr', formData.description_fr);
      data.append('description_en', formData.description_en);
      data.append('description_nl', formData.description_nl);
      data.append('category', formData.category);
      data.append('is_public', formData.is_public);
      
      if (formData.file) {
        data.append('file', formData.file);
      }

      const url = editingDoc 
        ? `${BACKEND_URL}/api/documents/${editingDoc.id}`
        : `${BACKEND_URL}/api/documents`;
      
      const method = editingDoc ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: data
      });

      if (response.ok) {
        setSuccess(editingDoc 
          ? (language === 'fr' ? 'Document mis à jour !' : 'Document updated!')
          : (language === 'fr' ? 'Document ajouté !' : 'Document added!')
        );
        loadDocuments();
        setTimeout(() => {
          setShowModal(false);
          resetForm();
        }, 1500);
      } else {
        const err = await response.json();
        setError(err.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (err) {
      console.error('Error saving document:', err);
      setError('Erreur de connexion');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer ce document ?' : 'Delete this document?')) {
      return;
    }

    try {
      const token = localStorage.getItem('ebb_token');
      const response = await fetch(`${BACKEND_URL}/api/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        loadDocuments();
      }
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const openEditModal = (doc) => {
    setEditingDoc(doc);
    setFormData({
      title_fr: doc.title_fr || '',
      title_en: doc.title_en || '',
      title_nl: doc.title_nl || '',
      description_fr: doc.description_fr || '',
      description_en: doc.description_en || '',
      description_nl: doc.description_nl || '',
      category: doc.category || 'general',
      is_public: doc.is_public !== false,
      file: null
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingDoc(null);
    setFormData({
      title_fr: '',
      title_en: '',
      title_nl: '',
      description_fr: '',
      description_en: '',
      description_nl: '',
      category: 'general',
      is_public: true,
      file: null
    });
    setError('');
    setSuccess('');
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) {
      return (
        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
          <span className="text-red-600 font-bold text-xs">PDF</span>
        </div>
      );
    }
    return <File className="w-8 h-8 text-blue-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {language === 'fr' ? 'Gestion des documents' : language === 'nl' ? 'Documentbeheer' : 'Document Management'}
              </h1>
              <p className="text-gray-500 text-sm">
                {language === 'fr' ? 'Uploadez et gérez vos fichiers téléchargeables' : 'Upload and manage your downloadable files'}
              </p>
            </div>
          </div>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            {language === 'fr' ? 'Ajouter un document' : 'Add document'}
          </button>
        </div>

        {/* Documents List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {language === 'fr' ? 'Aucun document. Ajoutez votre premier fichier !' : 'No documents. Add your first file!'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-700">
                    {language === 'fr' ? 'Document' : 'Document'}
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-gray-700">
                    {language === 'fr' ? 'Catégorie' : 'Category'}
                  </th>
                  <th className="text-center px-6 py-3 text-sm font-medium text-gray-700">
                    {language === 'fr' ? 'Taille' : 'Size'}
                  </th>
                  <th className="text-center px-6 py-3 text-sm font-medium text-gray-700">
                    {language === 'fr' ? 'Visibilité' : 'Visibility'}
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {getFileIcon(doc.file_type)}
                        <div>
                          <p className="font-medium text-gray-900">
                            {doc[`title_${language}`] || doc.title_fr || doc.filename}
                          </p>
                          <p className="text-sm text-gray-500 line-clamp-1">
                            {doc[`description_${language}`] || doc.description_fr || '-'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                        {categories.find(c => c.value === doc.category)?.label[language] || doc.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-500">
                      {formatFileSize(doc.file_size)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {doc.is_public ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                          <Globe className="w-3 h-3" />
                          Public
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                          Privé
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={language === 'fr' ? 'Voir' : 'View'}
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                        <a
                          href={doc.file_url}
                          download
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title={language === 'fr' ? 'Télécharger' : 'Download'}
                        >
                          <Download className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => openEditModal(doc)}
                          className="p-2 text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title={language === 'fr' ? 'Modifier' : 'Edit'}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title={language === 'fr' ? 'Supprimer' : 'Delete'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-medium text-blue-900 mb-2">
            {language === 'fr' ? 'Comment utiliser les documents' : 'How to use documents'}
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• {language === 'fr' ? 'Les documents publics apparaissent dans la page "Téléchargements" du site.' : 'Public documents appear in the "Downloads" page of the site.'}</li>
            <li>• {language === 'fr' ? 'Formats acceptés : PDF, JPG, PNG, WebP (max 10 MB)' : 'Accepted formats: PDF, JPG, PNG, WebP (max 10 MB)'}</li>
            <li>• {language === 'fr' ? 'Ajoutez des descriptions dans les 3 langues pour une meilleure expérience utilisateur.' : 'Add descriptions in all 3 languages for better user experience.'}</li>
          </ul>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingDoc 
                  ? (language === 'fr' ? 'Modifier le document' : 'Edit document')
                  : (language === 'fr' ? 'Ajouter un document' : 'Add document')
                }
              </h2>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* Error / Success */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
                  <Check className="w-4 h-4" />
                  {success}
                </div>
              )}

              {/* File Upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'fr' ? 'Fichier' : 'File'} {!editingDoc && '*'}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-amber-500 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                    required={!editingDoc}
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    {formData.file ? (
                      <p className="text-amber-600 font-medium">{formData.file.name}</p>
                    ) : editingDoc ? (
                      <p className="text-gray-500">
                        {language === 'fr' ? 'Cliquez pour remplacer le fichier' : 'Click to replace file'}
                        <br />
                        <span className="text-sm">({editingDoc.filename})</span>
                      </p>
                    ) : (
                      <p className="text-gray-500">
                        {language === 'fr' ? 'Cliquez ou glissez un fichier ici' : 'Click or drag a file here'}
                        <br />
                        <span className="text-sm">PDF, JPG, PNG, WebP (max 10 MB)</span>
                      </p>
                    )}
                  </label>
                </div>
              </div>

              {/* Category */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'fr' ? 'Catégorie' : 'Category'}
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label[language]}
                    </option>
                  ))}
                </select>
              </div>

              {/* Titles */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'fr' ? 'Titres' : 'Titles'}
                </label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="w-8 text-xs font-medium text-gray-500">FR</span>
                    <input
                      type="text"
                      value={formData.title_fr}
                      onChange={(e) => setFormData({ ...formData, title_fr: e.target.value })}
                      placeholder="Titre en français"
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      required
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-8 text-xs font-medium text-gray-500">EN</span>
                    <input
                      type="text"
                      value={formData.title_en}
                      onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                      placeholder="Title in English"
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-8 text-xs font-medium text-gray-500">NL</span>
                    <input
                      type="text"
                      value={formData.title_nl}
                      onChange={(e) => setFormData({ ...formData, title_nl: e.target.value })}
                      placeholder="Titel in het Nederlands"
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>
              </div>

              {/* Descriptions */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'fr' ? 'Descriptions' : 'Descriptions'}
                </label>
                <div className="space-y-3">
                  <div>
                    <span className="text-xs font-medium text-gray-500">FR</span>
                    <textarea
                      value={formData.description_fr}
                      onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
                      placeholder="Description en français..."
                      rows={2}
                      className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500">EN</span>
                    <textarea
                      value={formData.description_en}
                      onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                      placeholder="Description in English..."
                      rows={2}
                      className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500">NL</span>
                    <textarea
                      value={formData.description_nl}
                      onChange={(e) => setFormData({ ...formData, description_nl: e.target.value })}
                      placeholder="Beschrijving in het Nederlands..."
                      rows={2}
                      className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                </div>
              </div>

              {/* Public Toggle */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_public}
                    onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                    className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500"
                  />
                  <div>
                    <span className="font-medium text-gray-900">
                      {language === 'fr' ? 'Document public' : 'Public document'}
                    </span>
                    <p className="text-sm text-gray-500">
                      {language === 'fr' 
                        ? 'Visible dans la page Téléchargements du site' 
                        : 'Visible in the Downloads page of the site'}
                    </p>
                  </div>
                </label>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {language === 'fr' ? 'Enregistrement...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingDoc 
                        ? (language === 'fr' ? 'Mettre à jour' : 'Update')
                        : (language === 'fr' ? 'Ajouter' : 'Add')
                      }
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
